// Bar under the day tabs listing everything that narrows the jobs shown in the
// List and the Map: the pinned jobs first, then each active filter value
import { useState } from 'react';

import { XMarkIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';

import { identity, startCase } from 'lodash';

import {
  filterFields,
  schedulerFilterColumns,
  useSchedulerContext,
} from '@/context/SchedulerContext';

import BlockSchedulerOptionsOverlay from '@/components/Scheduler/BlockSchedulerOptionsOverlay';
import { PinIcon } from '@/components/common/PinToggle';

// Filter chips shown before the rest collapse into "+N more"
const MAX_COLLAPSED_FILTER_CHIPS = 4;

/**
 * Active filters bar: the Pinned chip, a chip per active filter value (each
 * with ✕ to remove it) and "Clear all". Renders nothing when no jobs are
 * pinned and no filters are active.
 * @returns {React.JSX.Element | null}
 */
export default function ActiveFiltersBar() {
  const {
    pinnedJobIds,
    clearPinnedJobs,
    activeFilters,
    activeFiltersCount,
    updateSchedulerFilterValue,
    clearActiveFilters,
  } = useSchedulerContext();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!pinnedJobIds.length && !activeFiltersCount) {
    return null;
  }

  const filterChips = filterFields.flatMap((field) => {
    const { label, formatOption = identity } = schedulerFilterColumns[field];
    return (activeFilters[field] || []).map((value) => ({
      key: `${field}:${value}`,
      text: `${label || startCase(field)}: ${formatOption(value)}`,
      onRemove: () => updateSchedulerFilterValue(field, value),
    }));
  });
  const shownFilterChips = isExpanded
    ? filterChips
    : filterChips.slice(0, MAX_COLLAPSED_FILTER_CHIPS);
  const hiddenCount = filterChips.length - shownFilterChips.length;

  const onClearAll = () => {
    clearPinnedJobs();
    clearActiveFilters();
  };

  return (
    <div className="active-filters-bar relative flex shrink-0 flex-wrap items-center gap-1.5 px-2 pt-2 text-sm md:px-5">
      {/* Locked like the other scheduler options while assignments are being edited */}
      <BlockSchedulerOptionsOverlay />
      <span className="text-xs font-semibold opacity-70">{'Filtered by:'}</span>
      {!!pinnedJobIds.length && (
        <FilterChip
          text={`Pinned: ${pinnedJobIds.length} ${pinnedJobIds.length === 1 ? 'job' : 'jobs'}`}
          removeLabel="Unpin all jobs"
          onRemove={clearPinnedJobs}
          icon={<PinIcon className="size-3.5" />}
          className="bg-primary-dark border-primary-dark text-white"
        />
      )}
      {shownFilterChips.map(({ key, text, onRemove }) => (
        <FilterChip
          key={key}
          text={text}
          removeLabel={`Remove filter ${text}`}
          onRemove={onRemove}
          className="badge-outline"
        />
      ))}
      {(hiddenCount > 0 || isExpanded) &&
        filterChips.length > MAX_COLLAPSED_FILTER_CHIPS && (
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Show less' : `+${hiddenCount} more`}
          </button>
        )}
      <button
        type="button"
        className="btn btn-ghost btn-xs text-accent"
        onClick={onClearAll}
      >
        {'Clear all'}
      </button>
    </div>
  );
}

/**
 * One removable chip in the active filters bar.
 * @param {object} props
 * @param {string} props.text
 * @param {string} props.removeLabel - Accessible name of the ✕ button.
 * @param {() => void} props.onRemove
 * @param {React.ReactNode} [props.icon]
 * @param {string} [props.className]
 * @returns {React.JSX.Element}
 */
function FilterChip({
  text,
  removeLabel,
  onRemove,
  icon,
  className,
}: {
  text: string;
  removeLabel: string;
  onRemove: () => void;
  icon?: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={clsx('badge max-w-64 gap-1 pr-0.5', className)}
      title={text}
    >
      {icon}
      <span className="truncate">{text}</span>
      <button
        type="button"
        className="btn btn-ghost btn-circle size-4 min-h-0 p-0 hover:bg-black/15"
        onClick={onRemove}
        aria-label={removeLabel}
        title={removeLabel}
      >
        <XMarkIcon className="size-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}
